import React from "react";
import { w2pt, izoom } from "../../utils/coords";

const SNAP_COLORS = {
  node:         "#22d3ee",   // cyan  — snapping TO an existing node (connection)
  intersection: "#e8a838",   // amber — line-line intersection
  edge:         "#a78bfa",   // purple — perpendicular point on edge
  midpoint:     "#4ade80",   // green  — midpoint of edge
};

/**
 * SnapDot — visual indicator for the current snap point.
 *
 * Node snap (cyan) is the most important: it means the new endpoint will
 * WELD to the existing node — confirming a connection.
 *
 * All snap types are rendered slightly smaller and subtler than before
 * to avoid visual noise, but the node snap has a distinctive double-ring
 * to make "you're about to connect" unmistakeable.
 */
export default function SnapDot({ snap, zoom }) {
  if (!snap || snap.type === "grid") return null;

  const s  = izoom(zoom);
  const sp = w2pt(snap.point);
  const C  = SNAP_COLORS[snap.type] || "#fff";

  return (
    <g style={{ pointerEvents: "none" }}>

      {/* Node snap — double ring, cyan, means "will connect here" */}
      {snap.type === "node" && (
        <>
          {/* Outer pulse ring */}
          <circle cx={sp.x} cy={sp.y} r={s * 9}
            fill="none" stroke={C} strokeWidth={s * 0.4} opacity={0.35}/>
          {/* Inner ring */}
          <circle cx={sp.x} cy={sp.y} r={s * 5}
            fill={C} fillOpacity={0.15} stroke={C} strokeWidth={s * 0.8} opacity={0.9}/>
          {/* Centre dot */}
          <circle cx={sp.x} cy={sp.y} r={s * 2}
            fill={C} opacity={0.95}/>
        </>
      )}

      {/* Intersection — amber × mark */}
      {snap.type === "intersection" && (
        <>
          <line x1={sp.x - s*5} y1={sp.y - s*5} x2={sp.x + s*5} y2={sp.y + s*5}
            stroke={C} strokeWidth={s * 0.9} opacity={0.85}/>
          <line x1={sp.x + s*5} y1={sp.y - s*5} x2={sp.x - s*5} y2={sp.y + s*5}
            stroke={C} strokeWidth={s * 0.9} opacity={0.85}/>
          <circle cx={sp.x} cy={sp.y} r={s * 1.5}
            fill={C} opacity={0.9}/>
        </>
      )}

      {/* Midpoint — green triangle */}
      {snap.type === "midpoint" && (
        <>
          <polygon
            points={`${sp.x},${sp.y - s*6} ${sp.x + s*5},${sp.y + s*3} ${sp.x - s*5},${sp.y + s*3}`}
            fill={C} fillOpacity={0.2} stroke={C} strokeWidth={s * 0.7} opacity={0.85}/>
          <circle cx={sp.x} cy={sp.y} r={s * 1.5} fill={C} opacity={0.9}/>
        </>
      )}

      {/* Edge perpendicular — purple square */}
      {snap.type === "edge" && (
        <>
          <rect x={sp.x - s*4} y={sp.y - s*4} width={s*8} height={s*8}
            fill="none" stroke={C} strokeWidth={s * 0.7} opacity={0.8}/>
          <circle cx={sp.x} cy={sp.y} r={s * 1.5} fill={C} opacity={0.9}/>
        </>
      )}

      {/* Snap type label — only for non-grid */}
      <text x={sp.x + s * 7} y={sp.y - s * 6}
        fontSize={s * 6} fill={C} fontFamily="'JetBrains Mono',monospace"
        opacity={0.7} style={{ pointerEvents:"none" }}>
        {snap.type === "node" ? "⊕ CONNECT" : snap.type.toUpperCase()}
      </text>

    </g>
  );
}
